import { ArrowRightIcon } from '@heroicons/react/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/solid';
import { Carousel } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import { motion } from 'framer-motion';
import { debounce } from 'lodash';
import Image, { StaticImageData } from 'next/image';
import Link from 'next/link';
import React, { useRef, useState } from 'react';

import ButtonDefault, { ButtonVariants } from '../../../shared/basic/button';
import { ShouldRender } from '../../../shared/basic/ShouldRender';

interface BookProps {
  id: number;
  title: string;
  desc: string;
  sideImageSrc: StaticImageData;
  frontImageSrc: StaticImageData;
  openImageSrc: StaticImageData;
  webFlowLink: string;
}

export type CustomCarouselRef = {
  next: () => void;
  prev: () => void;
  goTo: (slide: number) => void;
  autoPlay: (playType?: 'update' | 'leave' | 'blur' | undefined) => void;
  innerSlider: {
    goTo: (slide: number) => void;
  };
};

const BookList: React.FC<{ books: BookProps[] }> = ({ books }) => {
  const [hoveredBookId, setHoveredBookId] = useState<number>(0);
  const [isHovered, setIsHovered] = useState(false);
  const carouselRef = useRef<CustomCarouselRef | null>(null);
  const screens = useBreakpoint();

  const handleSlideChange = (currentSlide: number) => {
    setHoveredBookId(currentSlide);
  };

  const handleMouseEnter = debounce((index) => {
    setHoveredBookId(index);
    carouselRef.current?.goTo(index);
  }, 300);

  return (
    <div
      style={{
        boxShadow:
          '0px 4px 4px -1px rgba(0, 0, 0, 0.1), 0px -4px 4px -1px rgba(0, 0, 0, 0.1)',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className='px-4 xl:px-28 py-10'
    >
      <div className='relative'>
        <div className='flex absolute cursor-pointer  bottom-[50%] transform h-28 -translate-y-[-50%]  md:translate-y-[50%] items-center next-arrow'>
          <ShouldRender check={hoveredBookId !== 0}>
            <button
              disabled={hoveredBookId === 0}
              onClick={() => {
                handleMouseEnter(hoveredBookId - 1);
              }}
              className='z-10 w-8 h-8 xl:w-12 xl:h-12 rounded-full disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-400 disabled:border-gray-500  text-gray-800 flex cursor-pointer hover:bg-gray-50 justify-center items-center bg-white border border-gray-800'
            >
              <ChevronLeftIcon className=' w-4 h-4 xl:w-7 xl:h-7' />
            </button>
          </ShouldRender>
        </div>

        <div className='px-5 md:px-10 lg:px-16'>
          <Carousel
            afterChange={handleSlideChange}
            initialSlide={hoveredBookId}
            autoplay={!isHovered}
            dots={false}
            arrows={undefined}
            className=''
            ref={carouselRef as React.RefObject<CustomCarouselRef>}
          >
            {books?.map((book, index) => (
              <div key={`${book?.id}-${index}`} className='w-full '>
                <div className='flex flex-col gap-y-5 md:gap-y-0 md:flex-row'>
                  <div className='md:w-1/2 lg:w-7/12 xl:w-5/12 px-7 flex flex-col gap-y-5 py-10 justify-between'>
                    <div className='flex  flex-col gap-y-5 text-fadedWhite'>
                      <span className='text-2xl text-center md:text-start xl:text-[50px] xl:leading-10 flex min-h-16 xl:min-h-32 font-semibold '>
                        {hoveredBookId !== null
                          ? books[hoveredBookId]?.title
                          : ''}
                      </span>
                      <p className=' text-center xl:text-[20px] md:text-start'>
                        {hoveredBookId !== null
                          ? books[hoveredBookId]?.desc
                          : ''}
                      </p>
                    </div>

                    <div className='flex xs:flex-row gap-2 w-full lg:pr-20'>
                      <ButtonDefault
                        size={screens.xl ? 4 : 2}
                        rounded={true}
                        variant={ButtonVariants.PRIMARY}
                        onClick={() => {
                          if (window !== undefined && book?.webFlowLink) {
                            window.open(book.webFlowLink, '_blank');
                          }
                        }}
                        className='w-full '
                      >
                        <span className='flex justify-center text-xs gap-x-2 xl:text-[14px] whitespace-nowrap'>
                          LEARN MORE{' '}
                          <ArrowRightIcon className='w-4 h-4 text-white' />
                        </span>
                      </ButtonDefault>
                      <Link href='/product'>
                        <ButtonDefault
                          size={screens.xl ? 4 : 2}
                          rounded={true}
                          variant={ButtonVariants.WHITE}
                          className='text-primary py-1 w-full font-bold p-1 text-xs xl:text-[14px] whitespace-nowrap'
                        >
                          <span className='w-full'>EXPLORE ALL</span>
                        </ButtonDefault>
                      </Link>
                    </div>
                  </div>
                  <div className='flex justify-center  xl:w-full'>
                    <div className='flex relative w-2/3 md:w-full  justify-center items-center min-w-40'>
                      <Image
                        src={book.openImageSrc}
                        alt='BookImage'
                        width={screens.lg ? 650 : 500}
                        height={500}
                        className='object-contain '
                      />
                      {/* <motion.div
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      whileHover={{
                        scale: 1.1,
                        cursor: 'pointer',
                      }}
                      transition={{ duration: 0.3 }}
                      className='absolute'
                    >
                      <PlayIcon className='w-10 h-10 lg:w-20 lg:h-20 text-gray-900' />
                    </motion.div> */}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Carousel>
        </div>

        <div className='flex absolute cursor-pointer right-0 bottom-[50%] transform h-28 -translate-y-[-50%] md:translate-y-[50%] items-center next-arrow'>
          <ShouldRender check={hoveredBookId !== 8}>
            <button
              disabled={hoveredBookId >= 8}
              onClick={() => {
                handleMouseEnter(hoveredBookId + 1);
              }}
              className='z-10 w-8 h-8 xl:w-12 xl:h-12 rounded-full disabled:bg-gray-100 disabled:cursor-not-allowed  disabled:text-gray-400 disabled:border-gray-500  text-gray-800 flex cursor-pointer hover:bg-gray-50 justify-center items-center bg-white border border-gray-800'
            >
              <ChevronRightIcon className=' w-4 h-4 xl:w-7 xl:h-7 ' />
            </button>
          </ShouldRender>
        </div>
      </div>

      <div className='flex flex-wrap gap-y-2 md:flex-nowrap justify-between w-full pt-5 lg:pt-10 md:p-8 xl:pt-16'>
        {books?.map((book, index) => {
          const isSelected = hoveredBookId !== null && hoveredBookId === index;
          return (
            <motion.div
              key={`${book?.id}-${index}`}
              className={`h-auto flex  px-1  relative ${
                isSelected ? 'transform rotate-0' : 'transform rotate-[-10deg]'
              }`}
              onMouseEnter={() => handleMouseEnter(index)}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{
                scale: isSelected ? 1 : 1.1,
                cursor: 'pointer',
              }}
              transition={{ duration: 0.3 }}
            >
              <Image
                src={isSelected ? book.frontImageSrc : book.sideImageSrc}
                alt={book.title}
                width={200}
                height={200}
                className={
                  isSelected
                    ? '  h-20 w-14 md:w-24 md:h-36 lg:w-28 lg:h-40 xl:w-[220px] xl:h-[300px] '
                    : ' h-20 w-3 md:w-5 md:h-36 lg:w-6 lg:h-40 xl:w-[35px] xl:h-[300px] '
                }
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default BookList;
